/**
 * Game - Main Game Orchestrator
 * 
 * Central coordinator that ties all game systems together.
 * Manages initialization, update loop, and system communication.
 */

import { PhysicsWorld } from '../engine/PhysicsWorld';
import { GameLoop, FIXED_TIMESTEP } from '../engine/GameLoop';
import { Renderer } from '../engine/Renderer';
import { InputSystem } from '../engine/InputSystem';
import { Spawner } from './Spawner';
import { Conveyor } from './Conveyor';
import { Container } from './Container';
import { StateManager, Difficulty, GamePhase } from './StateManager';
import { Scoring, TJunctionResult } from './Scoring';
import { FillTracker } from './FillTracker';
import { Layout, LayoutDimensions } from './Layout';
import { DIFFICULTIES } from './Constants';

/** Game configuration */
export interface GameConfig {
    /** Canvas element to render to */
    canvas: HTMLCanvasElement;
    /** Initial difficulty (defaults to normal) */
    initialDifficulty?: Difficulty;
    /** Debug mode (shows extra info) */
    debug?: boolean;
}

/** Callbacks for UI integration */
export interface GameCallbacks {
    /** Called when phase changes */
    onPhaseChange?: (phase: GamePhase, oldPhase: GamePhase) => void;
    /** Called when finishing phase starts */
    onFinishingStart?: () => void;
    /** Called each second during finishing countdown */
    onTimerTick?: (secondsLeft: number) => void;
    /** Called when T-Junction is detected */
    onTJunction?: (result: TJunctionResult) => void;
    /** Called every frame with current game state (for HUD updates) */
    onStateUpdate?: (state: GameStateInfo) => void;
    /** Called when a package is grabbed */
    onGrab?: () => void;
    /** Called when a package is released (not thrown) */
    onRelease?: () => void;
    /** Called when a package is thrown (fast release) */
    onThrow?: () => void;
}

/** Game state info for HUD */
export interface GameStateInfo {
    phase: GamePhase;
    score: number;
    density: number;
    tJunctionCount: number;
    sortLeftPercent: number;
    timeLeft: number | null;
    isFinishing: boolean;
    fps: number;
}

/**
 * Game class - main orchestrator
 */
export class Game {
    // Configuration
    private canvas: HTMLCanvasElement;
    private debug: boolean;
    
    // Core engine systems
    private physics!: PhysicsWorld;
    private renderer!: Renderer;
    private input!: InputSystem;
    private loop!: GameLoop;
    
    // Game systems
    private layout!: Layout;
    private container!: Container;
    private spawner!: Spawner;
    private conveyor!: Conveyor;
    private stateManager!: StateManager;
    private scoring!: Scoring;
    private fillTracker!: FillTracker;
    
    // Timing
    private lastSpawnTime = 0;
    private frameCount = 0;
    
    // Callbacks
    private callbacks: GameCallbacks = {};
    
    // Initialization state
    private initialized = false;

    constructor(config: GameConfig) {
        this.canvas = config.canvas;
        this.debug = config.debug ?? false;
    }

    /**
     * Set callbacks for UI integration
     */
    setCallbacks(callbacks: GameCallbacks): void {
        this.callbacks = callbacks;
    }

    /**
     * Initialize all game systems.
     * Must be called before start().
     */
    async init(): Promise<void> {
        if (this.initialized) return;

        console.log('Initializing T-Stacker 2.0...');

        // Initialize physics
        this.physics = new PhysicsWorld();
        await this.physics.init();
        console.log('Physics initialized');

        // Initialize renderer
        this.renderer = new Renderer(this.canvas);
        console.log('Renderer initialized');

        // Initialize layout
        this.layout = new Layout();
        const dims = this.layout.calculate(
            this.renderer.getDimensions().width,
            this.renderer.getDimensions().height
        );
        console.log(`Layout calculated (viewport: ${dims.viewportWidth}x${dims.viewportHeight}, scale: ${dims.scaleFactor.toFixed(2)})`);

        // Initialize container
        this.container = new Container(this.physics);
        this.createContainer(dims);
        console.log('Container created');

        // Initialize spawner
        this.spawner = new Spawner(this.physics);
        this.spawner.setContainerBounds({
            left: dims.containerLeft,
            right: dims.containerRight,
            top: dims.containerTop,
            bottom: dims.containerBottom,
        });
        console.log('Spawner initialized');

        // Initialize conveyor
        this.conveyor = new Conveyor(this.physics, {
            baseSpeed: 80,
            speedMultiplier: DIFFICULTIES.normal.beltSpeed,
        });
        this.conveyor.setLayoutFromDimensions(
            dims.conveyorX,
            dims.conveyorY,
            dims.conveyorWidth,
            dims.conveyorHeight,
            dims.conveyorEndX,
            dims.conveyorSpawnX,
            dims.conveyorDetectionHeight,
            dims.conveyorRestingThreshold
        );
        console.log('Conveyor initialized');

        // Initialize input
        this.input = new InputSystem(this.physics, this.canvas);
        this.input.setScaledConfig({
            rotationMinTangentSpeed: dims.rotationMinTangentSpeed,
            rotationWarmupDistance: dims.rotationWarmupDistance,
            maxThrowSpeed: dims.maxThrowSpeed,
        });
        this.setupInputCallbacks();
        console.log('Input system initialized');

        // Initialize state manager
        this.stateManager = new StateManager();
        this.setupStateCallbacks();
        console.log('State manager initialized');

        // Initialize scoring
        this.scoring = new Scoring(this.physics, this.container);
        this.scoring.setGridCellSize(dims.scoringGridCellSize);
        this.setupScoringCallbacks();
        console.log('Scoring initialized');

        // Initialize fill tracker
        this.fillTracker = new FillTracker();
        const geometry = this.container.getGeometry();
        if (geometry) {
            this.fillTracker.setContainerArea(geometry.area);
        }
        console.log('Fill tracker initialized');

        // Create game loop
        this.loop = new GameLoop({
            update: (dt) => this.update(dt),
            render: (alpha) => this.render(alpha),
        });

        this.initialized = true;
        console.log('Game initialization complete');
    }

    /**
     * Create container from layout dimensions
     */
    private createContainer(dims: LayoutDimensions): void {
        this.container.create(
            dims.containerLeft,
            dims.containerRight,
            dims.containerTop,
            dims.containerBottom,
            dims.wallThickness
        );
    }

    /**
     * Setup input system callbacks
     */
    private setupInputCallbacks(): void {
        this.input.setOnGrab((body) => {
            if (this.debug) {
                console.log('Grabbed:', body.userData.shapeType, body.userData.id);
            }
            // Mark as held for conveyor to ignore
            body.userData.isHeld = true;
            // Notify for sound
            this.callbacks.onGrab?.();
        });

        this.input.setOnRelease((body, thrown) => {
            if (this.debug) {
                console.log('Released:', body.userData.shapeType, thrown ? '(thrown)' : '');
            }
            // Clear held flag
            body.userData.isHeld = false;
            // Notify for sound
            if (thrown) {
                this.callbacks.onThrow?.();
            } else {
                this.callbacks.onRelease?.();
            }
        });
    }

    /**
     * Setup state manager callbacks
     */
    private setupStateCallbacks(): void {
        this.stateManager.onPhaseChange((newPhase, oldPhase) => {
            console.log(`Phase: ${oldPhase} -> ${newPhase}`);
            
            // Handle phase-specific logic
            if (newPhase === 'playing') {
                this.onGameStart();
            } else if (newPhase === 'settling') {
                this.onSettlingStart();
            } else if (newPhase === 'gameover') {
                this.onGameOver();
            }
            
            this.callbacks.onPhaseChange?.(newPhase, oldPhase);
        });

        this.stateManager.onFinishingStart(() => {
            console.log('Finishing phase started');
            this.callbacks.onFinishingStart?.();
        });

        this.stateManager.onTimerTick((seconds) => {
            this.callbacks.onTimerTick?.(seconds);
        });
    }

    /**
     * Setup scoring callbacks
     */
    private setupScoringCallbacks(): void {
        this.scoring.onTJunction((result) => {
            if (this.debug) {
                console.log('T-Junction detected at', result.seamX.toFixed(0), result.celebrationY.toFixed(0));
            }
            this.callbacks.onTJunction?.(result);
        });
    }

    /**
     * Start the game loop.
     * Game starts in 'menu' phase by default.
     */
    start(): void {
        if (!this.initialized) {
            throw new Error('Game.init() must be called before start()');
        }
        
        this.loop.start();
        console.log('Game loop started');
    }

    /**
     * Stop the game loop
     */
    stop(): void {
        this.loop.stop();
    }

    /**
     * Start a new game with the given difficulty.
     * Called from menu UI.
     */
    startGame(difficulty: Difficulty): void {
        // Reset systems
        this.resetForNewGame();
        
        // Configure for difficulty
        const settings = DIFFICULTIES[difficulty];
        this.conveyor.setSpeedMultiplier(settings.beltSpeed);
        this.fillTracker.setFillThreshold(settings.fillThreshold);
        
        // Start game
        this.stateManager.startGame(difficulty);
    }

    /**
     * Retry with same difficulty
     */
    retry(): void {
        const difficulty = this.stateManager.getDifficulty();
        this.startGame(difficulty);
    }

    /**
     * Return to menu
     */
    returnToMenu(): void {
        this.stateManager.returnToMenu();
        // Could clean up packages here if needed
    }

    /**
     * Reset systems for a new game
     */
    private resetForNewGame(): void {
        // Clear all packages
        for (const body of this.physics.getAllBodies()) {
            this.physics.removeBody(body.handle);
        }
        
        // Reset trackers
        this.scoring.reset();
        this.fillTracker.reset();
        this.lastSpawnTime = 0;
        this.frameCount = 0;
        
        // Release any held body
        // (InputSystem doesn't expose a release method, but setting enabled=false then true works)
        this.input.setEnabled(false);
        this.input.setEnabled(true);
    }

    /**
     * Called when game starts playing
     */
    private onGameStart(): void {
        this.input.setEnabled(true);
        this.lastSpawnTime = performance.now();
    }

    /**
     * Called when settling phase starts
     */
    private onSettlingStart(): void {
        this.input.setEnabled(false);
        
        // Clear conveyor items
        this.conveyor.clearPackages();
        
        // Clear mid-air debris (packages above container or outside)
        this.clearDebris();
    }

    /**
     * Clear packages that are not in the container
     */
    private clearDebris(): void {
        const dims = this.layout.getDimensions();
        const toRemove: number[] = [];

        for (const body of this.physics.getAllBodies()) {
            const pos = this.physics.getPosition(body.handle);
            if (!pos) continue;

            // Remove if above container top or outside container X bounds
            if (pos.y < dims.containerTop - 50 || 
                pos.x < dims.containerLeft - 50 || 
                pos.x > dims.containerRight + 50) {
                toRemove.push(body.handle);
            }
        }

        for (const handle of toRemove) {
            this.physics.removeBody(handle);
        }

        if (toRemove.length > 0) {
            console.log(`Cleared ${toRemove.length} debris packages`);
        }
    }

    /**
     * Called when game ends
     */
    private onGameOver(): void {
        // Final scoring update
        const settings = this.stateManager.getDifficultySettings();
        this.scoring.update(settings.tJunctionBonus);
    }

    // ==================== UPDATE LOOP ====================

    /**
     * Main update function (called at fixed timestep)
     */
    private update(dt: number): void {
        const phase = this.stateManager.getPhase();
        const now = performance.now();

        // Update state manager (handles timers)
        this.stateManager.update(now);

        if (phase === 'playing') {
            this.updatePlaying(dt, now);
        } else if (phase === 'settling') {
            this.updateSettling(dt);
        }

        // Step physics (always, even during menu for visual demos)
        if (phase === 'playing' || phase === 'settling') {
            this.physics.step(FIXED_TIMESTEP);
        }

        // Capture state for interpolation
        this.renderer.captureState(this.physics);
        
        this.frameCount++;
    }

    /**
     * Update during playing phase
     */
    private updatePlaying(_dt: number, now: number): void {
        // Update input
        this.input.update(FIXED_TIMESTEP);

        // Update conveyor
        this.conveyor.update(FIXED_TIMESTEP);

        // Check for spawning
        if (!this.stateManager.getIsFinishing()) {
            this.checkSpawning(now);
        }

        // Check for finishing phase trigger
        if (!this.stateManager.getIsFinishing() && this.fillTracker.hasReachedThreshold()) {
            this.stateManager.startFinishingPhase();
        }

        // Update scoring every 10 frames
        if (this.frameCount % 10 === 0) {
            const settings = this.stateManager.getDifficultySettings();
            this.scoring.update(settings.tJunctionBonus);
        }
    }

    /**
     * Check if we should spawn a new package
     */
    private checkSpawning(now: number): void {
        const settings = this.stateManager.getDifficultySettings();
        const elapsed = now - this.lastSpawnTime;

        if (elapsed >= settings.spawnInterval) {
            this.spawnPackage();
            this.lastSpawnTime = now;
        }
    }

    /**
     * Spawn a new package on the conveyor
     */
    private spawnPackage(): void {
        // Estimate height for spawn position
        const estimatedHeight = 80;
        const spawnPos = this.layout.getSpawnPosition(estimatedHeight);
        
        // Spawn random package
        const body = this.spawner.spawnRandom(spawnPos.x, spawnPos.y);
        
        // Set upright rotation for conveyor
        this.physics.setRotation(body.handle, 0);
        
        // Track for fill percentage
        this.fillTracker.trackSpawn(body);
        
        if (this.debug) {
            console.log(`Spawned ${body.userData.shapeType}, fill: ${this.fillTracker.getSortLeftPercent()}%`);
        }
    }

    /**
     * Update during settling phase
     */
    private updateSettling(_dt: number): void {
        // Continue scoring (less frequently)
        if (this.frameCount % 30 === 0) {
            const settings = this.stateManager.getDifficultySettings();
            this.scoring.update(settings.tJunctionBonus);
        }
    }

    // ==================== RENDER LOOP ====================

    /**
     * Main render function (called every frame with interpolation alpha)
     */
    private render(alpha: number): void {
        const dims = this.layout.getDimensions();
        const ctx = (this.renderer as unknown as { ctx: CanvasRenderingContext2D }).ctx;

        // Clear entire screen
        this.renderer.clear();

        // Check if we need letterboxing
        const needsLetterbox = dims.viewportOffsetX > 0;
        
        if (needsLetterbox && ctx) {
            // Clip to viewport area so nothing renders outside
            ctx.save();
            ctx.beginPath();
            ctx.rect(dims.viewportOffsetX, 0, dims.viewportWidth, dims.viewportHeight);
            ctx.clip();
        }

        // Draw conveyor
        const convGeo = this.conveyor.getGeometry();
        this.renderer.renderConveyor(
            convGeo.x,
            convGeo.y,
            convGeo.width,
            convGeo.height,
            this.conveyor.getAnimationOffset(),
            dims.chevronSpacing,
            dims.chevronSize
        );

        // Draw container
        this.renderer.renderContainer(
            dims.containerBottom,
            dims.containerLeft,
            dims.containerRight,
            dims.wallThickness,
            dims.containerTop
        );

        // Draw fill line
        this.renderFillLine(dims);

        // Draw packages
        const heldBody = this.input.getHeldBody();
        this.renderer.renderBodies(this.physics, alpha, heldBody?.handle);

        // Draw spring line if holding
        const springLine = this.input.getSpringLine();
        if (springLine) {
            this.renderer.renderSpringLine(springLine);
        }

        // Restore clipping if we applied it
        if (needsLetterbox && ctx) {
            ctx.restore();
            
            // Draw letterbox bars on top (outside the clipped area)
            const letterboxes = this.layout.getLetterboxRegions();
            for (const box of letterboxes) {
                this.renderLetterbox(box);
            }
        }

        // Debug info (outside letterbox so it's always visible)
        if (this.debug) {
            this.renderDebugInfo();
        }

        // FPS (outside letterbox so it's always visible)
        this.renderer.renderFps(this.loop.getFps());

        // Notify UI of state update
        this.notifyStateUpdate();
    }

    /**
     * Render letterbox region
     */
    private renderLetterbox(box: { x: number; y: number; width: number; height: number }): void {
        const ctx = (this.renderer as unknown as { ctx: CanvasRenderingContext2D }).ctx;
        if (!ctx) return;
        
        ctx.fillStyle = '#000000';
        ctx.fillRect(box.x, box.y, box.width, box.height);
    }

    /**
     * Render fill line at container top
     */
    private renderFillLine(dims: LayoutDimensions): void {
        const ctx = (this.renderer as unknown as { ctx: CanvasRenderingContext2D }).ctx;
        if (!ctx) return;

        ctx.save();
        ctx.setLineDash([10, 10]);
        ctx.strokeStyle = '#666666';
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.4;
        ctx.beginPath();
        ctx.moveTo(dims.containerLeft, dims.containerTop);
        ctx.lineTo(dims.containerRight, dims.containerTop);
        ctx.stroke();
        ctx.restore();
    }

    /**
     * Render debug information
     */
    private renderDebugInfo(): void {
        const info = [
            `Phase: ${this.stateManager.getPhase()}`,
            `Bodies: ${this.physics.getAllBodies().length}`,
            `On conveyor: ${this.conveyor.getPackageCount()}`,
            `Density: ${this.scoring.getDensity().toFixed(1)}%`,
            `T-Junctions: ${this.scoring.getTJunctionCount()}`,
            `Sort Left: ${this.fillTracker.getSortLeftPercent()}%`,
        ];
        
        const timeLeft = this.stateManager.getTimeLeft();
        if (timeLeft !== null) {
            info.push(`Time: ${timeLeft}s`);
        }

        this.renderer.renderDebugInfo(info);
    }

    /**
     * Notify UI callback of current state
     */
    private notifyStateUpdate(): void {
        if (!this.callbacks.onStateUpdate) return;

        const settings = this.stateManager.getDifficultySettings();
        const scoringState = this.scoring.getState(settings.tJunctionBonus);

        const state: GameStateInfo = {
            phase: this.stateManager.getPhase(),
            score: scoringState.totalScore,
            density: scoringState.density,
            tJunctionCount: scoringState.tJunctionCount,
            sortLeftPercent: this.fillTracker.getSortLeftPercent(),
            timeLeft: this.stateManager.getTimeLeft(),
            isFinishing: this.stateManager.getIsFinishing(),
            fps: this.loop.getFps(),
        };

        this.callbacks.onStateUpdate(state);
    }

    // ==================== PUBLIC GETTERS ====================

    /**
     * Get current game state info
     */
    getStateInfo(): GameStateInfo {
        const settings = this.stateManager.getDifficultySettings();
        const scoringState = this.scoring.getState(settings.tJunctionBonus);

        return {
            phase: this.stateManager.getPhase(),
            score: scoringState.totalScore,
            density: scoringState.density,
            tJunctionCount: scoringState.tJunctionCount,
            sortLeftPercent: this.fillTracker.getSortLeftPercent(),
            timeLeft: this.stateManager.getTimeLeft(),
            isFinishing: this.stateManager.getIsFinishing(),
            fps: this.loop.getFps(),
        };
    }

    /**
     * Get the layout dimensions
     */
    getLayout(): LayoutDimensions {
        return this.layout.getDimensions();
    }

    /**
     * Get the state manager (for UI integration)
     */
    getStateManager(): StateManager {
        return this.stateManager;
    }

    /**
     * Check if game is initialized
     */
    isInitialized(): boolean {
        return this.initialized;
    }

    /**
     * Clean up resources
     */
    destroy(): void {
        this.loop.stop();
        this.input.destroy();
        this.conveyor.destroy();
        this.container.destroy();
        this.physics.destroy();
    }
}
